import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LogIn, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';

// People Logo
const PeopleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="6" r="3.5" />
    <path d="M6 17.5c0-3.5 2.7-6 6-6s6 2.5 6 6v1.5H6v-1.5z" />
    <circle cx="4" cy="8" r="2" opacity="0.6" />
    <path d="M0 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1H0v-1z" opacity="0.6" />
    <circle cx="20" cy="8" r="2" opacity="0.6" />
    <path d="M16 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1h-8v-1z" opacity="0.6" />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { login } = useApp();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await api.login(formData.email, formData.password);
      
      // Check if 2FA is required
      if (result.requires_2fa) {
        setRequires2FA(true);
        setTwoFAUserId(result.user_id);
        toast.info('Veuillez entrer votre code d\'authentification à deux facteurs');
        setLoading(false);
        return;
      }
      
      if (result.success && result.user) {
        // Pass tokens to login function for secure storage
        login(result.user, {
          access_token: result.access_token,
          refresh_token: result.refresh_token
        });
        toast.success(`Bienvenue ${result.user.prenom} !`);
        navigate('/');
      }
    } catch (error) {
      // Handle rate limiting error
      if (error.message.includes('Trop de requêtes') || error.message.includes('429')) {
        toast.error('Trop de tentatives de connexion. Veuillez patienter quelques minutes.');
      } else {
        toast.error(error.message || 'Email ou mot de passe incorrect');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    
    if (!twoFACode || twoFACode.length < 6) {
      toast.error('Veuillez entrer un code à 6 chiffres ou un code de secours');
      return;
    }
    
    setVerifying2FA(true);
    
    try {
      const result = await api.verify2FA(twoFAUserId, twoFACode);
      
      if (result.success && result.user) {
        login(result.user, {
          access_token: result.access_token,
          refresh_token: result.refresh_token
        });
        toast.success(`Bienvenue ${result.user.prenom} !`);
        navigate('/');
      }
    } catch (error) {
      toast.error(error.message || 'Code 2FA invalide');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFAUserId(null);
    setTwoFACode('');
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl bg-green-600 flex items-center justify-center">
                <ShieldCheck className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl">Vérification 2FA</CardTitle>
            <CardDescription>
              Entrez le code de votre application d'authentification
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handle2FASubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="2fa-code">Code d'authentification</Label>
                <Input
                  id="2fa-code"
                  type="text"
                  placeholder="000000 ou CODE1234"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase())}
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={8}
                  autoFocus
                  required
                  data-testid="2fa-login-code"
                />
                <p className="text-xs text-gray-500 text-center mt-2">
                  Entrez le code à 6 chiffres ou un code de secours à 8 caractères
                </p>
              </div>
              
              <Alert className="bg-blue-50 border-blue-200">
                <ShieldCheck className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  Vous pouvez aussi utiliser un de vos <strong>codes de secours</strong> à 8 caractères.
                </AlertDescription>
              </Alert>
              
              <Button type="submit" className="w-full" disabled={verifying2FA}>
                {verifying2FA ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Vérifier
                  </>
                )}
              </Button>
              
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={handleBack}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à la connexion
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center">
              <PeopleIcon className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">People Management</CardTitle>
          <CardDescription>Connectez-vous à votre compte</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre.email@jeldwen.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="login-email"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                  Mot de passe oublié ?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="login-password"
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Se connecter
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Vous avez reçu une invitation ?</p>
            <p className="mt-1">Utilisez le lien dans l'email pour créer votre compte.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
